import type { MapDef } from '../world/mapdef';
import { tileAt } from '../world/map';
import { BUILDING_WALLS, ROOFS, T } from '../world/tiles';
import { fbm, hash2, hex, mix, noise2, smoothstep, type RGB } from './noise';
import { sun, type Ctx } from './paint';

/**
 * Everything that makes an outdoor map feel like a place: the sky and the
 * distant horizon beyond the map edges, shadows that buildings cast on the
 * ground, birds crossing the sky, and the colour grade of each hour.
 */

export type Biome = 'city' | 'mountain' | 'coast' | 'town' | 'none';

const CITY = new Set(['taipei', 'ximen', 'longshan', 'cks']);
const MOUNTAIN = new Set(['beitou', 'hualien']);
const COAST = new Set(['hlcity']);

export function biomeOf(map: MapDef): Biome {
  if (map.indoor) return 'none';
  if (CITY.has(map.id)) return 'city';
  if (MOUNTAIN.has(map.id)) return 'mountain';
  if (COAST.has(map.id)) return 'coast';
  return 'town';
}

// --- Sky colours by the minute ------------------------------------------------------

interface SkyKey {
  at: number;
  top: RGB;
  horizon: RGB;
}
const SKY: SkyKey[] = [
  { at: 0, top: hex('#070a1c'), horizon: hex('#161c3c') },
  { at: 5 * 60, top: hex('#0a1030'), horizon: hex('#262a58') },
  { at: 6 * 60 + 30, top: hex('#3b4a8c'), horizon: hex('#ffa070') },
  { at: 8 * 60, top: hex('#5aa0e6'), horizon: hex('#d6ecfa') },
  { at: 12 * 60, top: hex('#4a97ea'), horizon: hex('#dcf0fc') },
  { at: 16 * 60 + 30, top: hex('#5a95d8'), horizon: hex('#f6dfb6') },
  { at: 17 * 60 + 45, top: hex('#44509c'), horizon: hex('#ff9a58') },
  { at: 19 * 60, top: hex('#1e2250'), horizon: hex('#c26a80') },
  { at: 20 * 60 + 30, top: hex('#0a0f2a'), horizon: hex('#1d2452') },
  { at: 24 * 60, top: hex('#070a1c'), horizon: hex('#161c3c') },
];

export function skyColors(minutes: number): { top: RGB; horizon: RGB } {
  let a = SKY[0], b = SKY[SKY.length - 1];
  for (let i = 0; i < SKY.length - 1; i++) {
    if (minutes >= SKY[i].at && minutes < SKY[i + 1].at) {
      a = SKY[i];
      b = SKY[i + 1];
      break;
    }
  }
  const k = b.at === a.at ? 0 : Math.max(0, Math.min(1, (minutes - a.at) / (b.at - a.at)));
  return { top: mix(a.top, b.top, k), horizon: mix(a.horizon, b.horizon, k) };
}

const rgb = (c: RGB, a = 1) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`;

export interface View {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** How dark it is, 0 (day) → 1 (night). */
export function nightOf(minutes: number): number {
  return Math.max(0, Math.min(1, (minutes - 18 * 60) / 120)) * (1 - smoothstep(5 * 60, 7 * 60, minutes));
}

// --- Backdrop: sky, horizon silhouettes and the void beyond the map ----------------------

/** Draw what lies beyond the map's edges. Expects the world transform (camera applied). */
export function drawBackdrop(c: Ctx, map: MapDef, v: View, minutes: number, t: number): void {
  const biome = biomeOf(map);
  const mw = map.w * T, mh = map.h * T;
  const night = nightOf(minutes);
  const sky = skyColors(minutes);

  if (biome === 'none') {
    c.fillStyle = '#0d0a12';
    c.fillRect(v.x, v.y, v.w, v.h);
    return;
  }

  // Ground-level "beyond": dark earth with a faint texture, under everything.
  const base = biome === 'city' ? '#141220' : biome === 'coast' ? '#101a22' : '#0f1a14';
  c.fillStyle = base;
  c.fillRect(v.x, v.y, v.w, v.h);

  // Sky above the map's top edge.
  if (v.y < 0) {
    const top = Math.min(v.y, -400);
    const g = c.createLinearGradient(0, top, 0, 0);
    g.addColorStop(0, rgb(sky.top));
    g.addColorStop(0.75, rgb(mix(sky.top, sky.horizon, 0.55)));
    g.addColorStop(1, rgb(sky.horizon));
    c.fillStyle = g;
    c.fillRect(v.x, top, v.w, -top);
    stars(c, v, night, t);
    moonAndSun(c, v, minutes, night);
    skyClouds(c, v, t, night);
    horizon(c, map, v, biome, sky, night, t);
    // Haze right at the horizon so silhouettes melt into the sky.
    const h = c.createLinearGradient(0, -22, 0, 0);
    h.addColorStop(0, rgb(sky.horizon, 0));
    h.addColorStop(1, rgb(sky.horizon, 0.35));
    c.fillStyle = h;
    c.fillRect(v.x, -22, v.w, 22);
  }

  // Below the map: a dark treeline / rooftops band.
  if (v.y + v.h > mh) {
    const col = biome === 'city' ? '#1a1726' : biome === 'coast' ? '#14222a' : '#152419';
    c.fillStyle = col;
    c.beginPath();
    c.moveTo(v.x - 10, mh + 60);
    for (let x = Math.floor(v.x / 8) * 8 - 8; x <= v.x + v.w + 8; x += 8) {
      const b = biome === 'city' ? Math.floor(noise2(x / 40, 7, 821) * 3) * 6 : noise2(x / 14, 3, 822) * 14;
      c.lineTo(x, mh + 6 + b);
    }
    c.lineTo(v.x + v.w + 10, mh + 60);
    c.closePath();
    c.fill();
    c.fillStyle = 'rgba(0,0,0,0.35)';
    c.fillRect(v.x, mh + 40, v.w, v.h);
  }
  // Beside the map: fade to black so the edge never looks like a cliff.
  if (v.x < 0) {
    const g = c.createLinearGradient(-40, 0, 0, 0);
    g.addColorStop(0, 'rgba(0,0,0,0.5)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    c.fillStyle = g;
    c.fillRect(-40, v.y, 40, v.h);
  }
  if (v.x + v.w > mw) {
    const g = c.createLinearGradient(mw, 0, mw + 40, 0);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, 'rgba(0,0,0,0.5)');
    c.fillStyle = g;
    c.fillRect(mw, v.y, 40, v.h);
  }
}

function stars(c: Ctx, v: View, night: number, t: number): void {
  if (night < 0.2) return;
  const a = (night - 0.2) / 0.8;
  for (let i = 0; i < 70; i++) {
    const sx = v.x + hash2(i, 1, 701) * v.w * 1.2 - v.w * 0.1 + (hash2(i, 2, 702) - 0.5) * 0;
    const sy = -8 - hash2(i, 3, 703) * 150;
    if (sy < v.y) continue;
    const tw = 0.5 + 0.5 * Math.sin(t * (1 + hash2(i, 4, 704) * 2) + i);
    c.fillStyle = `rgba(255,250,230,${a * (0.35 + tw * 0.65)})`;
    const s = hash2(i, 5, 705) > 0.85 ? 1.5 : 1;
    c.fillRect(sx, sy, s, s);
  }
}

function moonAndSun(c: Ctx, v: View, minutes: number, night: number): void {
  const cx = v.x + v.w * 0.72;
  if (night > 0.35) {
    const y = -58 - (night - 0.35) * 30;
    c.fillStyle = `rgba(255,248,220,${0.85 * night})`;
    c.beginPath();
    c.arc(cx, y, 7, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = `rgba(255,255,255,${0.12 * night})`;
    c.beginPath();
    c.arc(cx, y, 14, 0, Math.PI * 2);
    c.fill();
    return;
  }
  // The sun sits low at dawn and dusk; at midday it is out of frame.
  const k = Math.abs(minutes - 12.5 * 60) / (6 * 60);
  if (k < 0.55) return;
  const y = -40 - (1 - k) * 150;
  const warm = smoothstep(0.55, 1, k);
  c.save();
  c.globalCompositeOperation = 'lighter';
  const g = c.createRadialGradient(cx, y, 2, cx, y, 60);
  g.addColorStop(0, `rgba(255,236,160,${0.9 * warm})`);
  g.addColorStop(0.2, `rgba(255,190,110,${0.35 * warm})`);
  g.addColorStop(1, 'rgba(255,160,80,0)');
  c.fillStyle = g;
  c.fillRect(cx - 60, y - 60, 120, 120);
  c.restore();
}

function skyClouds(c: Ctx, v: View, t: number, night: number): void {
  const a = 0.55 - night * 0.4;
  for (let i = 0; i < 5; i++) {
    const span = v.w + 240;
    const x = v.x - 120 + ((hash2(i, 9, 711) * span + t * (4 + i)) % span);
    const y = -70 - hash2(i, 8, 712) * 110;
    if (y < v.y - 30) continue;
    const w = 26 + hash2(i, 7, 713) * 40;
    c.fillStyle = `rgba(255,255,255,${a})`;
    for (let k = 0; k < 4; k++) {
      const ox = (k - 1.5) * w * 0.3, r = w * (0.22 + hash2(i * 4 + k, 6, 714) * 0.16);
      c.beginPath();
      c.ellipse(x + ox, y + (k % 2) * 2, r, r * 0.6, 0, 0, Math.PI * 2);
      c.fill();
    }
  }
}

/** Distant silhouettes with parallax: slower layers are further away and bluer. */
function horizon(c: Ctx, map: MapDef, v: View, biome: Biome, sky: { top: RGB; horizon: RGB }, night: number, t: number): void {
  const haze = mix(sky.horizon, sky.top, 0.3);
  if (biome === 'mountain' || biome === 'coast' || biome === 'town') {
    const layers = biome === 'town' ? [[0.25, 34, 0.62], [0.5, 20, 0.35]] : [[0.18, 62, 0.65], [0.36, 44, 0.42], [0.58, 26, 0.2]];
    const near: RGB = biome === 'coast' ? hex('#24403a') : biome === 'town' ? hex('#2e5a3a') : hex('#223d2e');
    layers.forEach(([par, amp, far], li) => {
      const col = mix(near, haze, far * (1 - night * 0.4));
      c.fillStyle = rgb(mix(col, hex('#0a0f20'), night * 0.5));
      c.beginPath();
      c.moveTo(v.x - 10, 2);
      const seed = 731 + li * 7;
      for (let x = Math.floor(v.x / 6) * 6 - 6; x <= v.x + v.w + 6; x += 6) {
        const px = x + v.x * par; // parallax: the layer slides slower than the camera
        const h = fbm(px / (biome === 'town' ? 140 : 90), li, seed, 3) * amp + smoothstep(0.4, 0.9, noise2(px / 180, li, seed + 3)) * amp * 0.8;
        c.lineTo(x, -h - (biome === 'town' ? 2 : 6));
      }
      c.lineTo(v.x + v.w + 10, 2);
      c.closePath();
      c.fill();
    });
    if (biome === 'coast') seaLine(c, v, haze, night, t);
    if (biome === 'town') rooftops(c, v, 0.75, night, haze);
    return;
  }
  // City: two rows of towers, the far one pale, the near one dark with lit windows.
  const far: RGB = mix(hex('#5b6a8c'), haze, 0.55);
  towers(c, map, v, 0.3, 24, 58, far, night, 751, false);
  towers(c, map, v, 0.6, 12, 36, mix(hex('#262a44'), haze, 0.2), night, 761, true);
}

function towers(c: Ctx, map: MapDef, v: View, par: number, hMin: number, hMax: number, col: RGB, night: number, seed: number, windows: boolean): void {
  const dark = mix(col, hex('#0a0d1e'), night * 0.6);
  c.fillStyle = rgb(dark);
  let x = Math.floor((v.x + v.x * par) / 14) * 14 - 28;
  const end = v.x + v.w + v.x * par + 28;
  const landmark = map.id !== 'taipei';
  while (x < end) {
    const i = Math.floor(x / 14);
    const w = 10 + Math.floor(hash2(i, 1, seed) * 3) * 4;
    const h = hMin + hash2(i, 2, seed) * (hMax - hMin);
    const sx = x - v.x * par;
    c.fillStyle = rgb(dark);
    c.fillRect(sx, -h, w, h + 2);
    // Rooftop details: water tanks and antennas.
    if (hash2(i, 3, seed) > 0.6) c.fillRect(sx + 2, -h - 3, 3, 3);
    if (hash2(i, 4, seed) > 0.8) c.fillRect(sx + w - 3, -h - 6, 1, 6);
    if (windows && night > 0.25) {
      c.fillStyle = `rgba(255,220,140,${0.6 * night})`;
      for (let wy = -h + 3; wy < -3; wy += 4) for (let wx = 1.5; wx < w - 1; wx += 3) if (hash2(i * 97 + wx, wy, seed + 1) > 0.55) c.fillRect(sx + wx, wy, 1.5, 2);
    }
    // Taipei 101 on the skyline, unless we are standing right under it.
    if (landmark && !windows && i % 97 === 40) tower101(c, sx + w / 2, dark, night);
    x += w + 2 + Math.floor(hash2(i, 5, seed) * 2) * 4;
  }
}

function tower101(c: Ctx, x: number, col: RGB, night: number): void {
  c.fillStyle = rgb(col);
  c.fillRect(x - 7, -40, 14, 42);
  for (let k = 0; k < 8; k++) {
    const y = -40 - k * 9;
    c.beginPath();
    c.moveTo(x - 6, y);
    c.lineTo(x + 6, y);
    c.lineTo(x + 7.5, y - 8);
    c.lineTo(x - 7.5, y - 8);
    c.closePath();
    c.fill();
  }
  c.fillRect(x - 3, -118, 6, 8);
  c.fillRect(x - 0.5, -140, 1, 24);
  if (night > 0.3) {
    c.fillStyle = `rgba(120,200,255,${0.7 * night})`;
    for (let k = 0; k < 8; k++) c.fillRect(x - 5, -41 - k * 9, 10, 1);
    c.fillStyle = `rgba(255,80,80,${(0.5 + 0.5 * Math.sin(performance.now() / 400)) * night})`;
    c.fillRect(x - 1, -141, 2, 2);
  }
}

function rooftops(c: Ctx, v: View, par: number, night: number, haze: RGB): void {
  // A village of low houses on the far side of the fields: varied widths, pitched roofs, the odd chimney.
  const col = mix(hex('#4a4058'), haze, 0.3 * (1 - night));
  let x = Math.floor((v.x + v.x * par) / 18) * 18 - 36;
  const end = v.x + v.w + v.x * par + 36;
  while (x < end) {
    const i = Math.floor(x / 18);
    const w = 10 + Math.floor(hash2(i, 1, 771) * 4) * 4, h = 5 + hash2(i, 2, 771) * 7;
    const sx = x - v.x * par;
    const gap = hash2(i, 6, 771) < 0.25;
    if (!gap) {
      c.fillStyle = rgb(mix(col, hex('#0a0d1e'), night * 0.6));
      c.fillRect(sx, -h, w, h + 2);
      c.beginPath();
      c.moveTo(sx - 1, -h);
      c.lineTo(sx + w * (0.3 + hash2(i, 7, 771) * 0.4), -h - 3 - hash2(i, 8, 771) * 4);
      c.lineTo(sx + w + 1, -h);
      c.fill();
      if (hash2(i, 9, 771) > 0.6) c.fillRect(sx + w - 4, -h - 5, 1.5, 5);
      if (night > 0.3 && hash2(i, 3, 771) > 0.5) {
        c.fillStyle = `rgba(255,220,140,${0.6 * night})`;
        c.fillRect(sx + 2 + hash2(i, 4, 771) * (w - 5), -h + 2, 2, 2);
      }
    }
    x += w + 2 + Math.floor(hash2(i, 5, 771) * 3) * 4;
  }
}

function seaLine(c: Ctx, v: View, haze: RGB, night: number, t: number): void {
  // A strip of Pacific peeking between the hills on the right.
  const x0 = v.x + v.w * 0.55;
  const g = c.createLinearGradient(0, -14, 0, 0);
  g.addColorStop(0, rgb(mix(hex('#3a86b8'), haze, 0.5 + night * 0.3)));
  g.addColorStop(1, rgb(mix(hex('#2c6f9c'), haze, 0.3 + night * 0.3)));
  c.fillStyle = g;
  c.fillRect(x0, -14, v.x + v.w - x0 + 10, 16);
  c.fillStyle = `rgba(255,255,255,${0.35 - night * 0.25})`;
  for (let i = 0; i < 12; i++) {
    const wx = x0 + ((hash2(i, 1, 781) * 200 + t * 3) % (v.w * 0.5));
    c.fillRect(wx, -12 + hash2(i, 2, 781) * 10, 4 + hash2(i, 3, 781) * 6, 0.6);
  }
}

// --- Cast shadows of buildings, walls and hedges --------------------------------------

interface Run {
  x0: number;
  x1: number;
  y: number;
  /** Tile rows of structure above the bottom edge (taller things throw longer shadows). */
  height: number;
  soft: boolean;
}

const BUILDING = new Set<string>([...BUILDING_WALLS, ...ROOFS, 'J', 'O', '3', '6', '9']);
const SOFT = new Set<string>(['h', 'M', '?']);
const runCache = new Map<string, Run[]>();

function runsOf(map: MapDef): Run[] {
  let runs = runCache.get(map.id);
  if (runs) return runs;
  runs = [];
  for (let ty = 0; ty < map.h; ty++) {
    let tx = 0;
    while (tx < map.w) {
      const ch = tileAt(map, tx, ty);
      const kind = BUILDING.has(ch) ? 'hard' : SOFT.has(ch) ? 'soft' : null;
      const below = tileAt(map, tx, ty + 1);
      const casts = kind && !BUILDING.has(below) && !SOFT.has(below) && below !== ' ';
      if (!casts) {
        tx++;
        continue;
      }
      let x1 = tx;
      while (x1 + 1 < map.w) {
        const n = tileAt(map, x1 + 1, ty), nb = tileAt(map, x1 + 1, ty + 1);
        const nk = BUILDING.has(n) ? 'hard' : SOFT.has(n) ? 'soft' : null;
        if (nk !== kind || BUILDING.has(nb) || SOFT.has(nb) || nb === ' ') break;
        x1++;
      }
      let height = 1;
      while (height < 8 && (BUILDING.has(tileAt(map, tx, ty - height)) || SOFT.has(tileAt(map, tx, ty - height)))) height++;
      runs.push({ x0: tx * T, x1: (x1 + 1) * T, y: (ty + 1) * T, height, soft: kind === 'soft' });
      tx = x1 + 1;
    }
  }
  runCache.set(map.id, runs);
  return runs;
}

/** Soft shadows on the ground along the sunlit side of every structure. World transform. */
export function drawBuildingShadows(c: Ctx, map: MapDef, v: View): void {
  if (map.indoor) return;
  const runs = runsOf(map);
  const reach = 0.45 + sun.len * 1.3;
  c.save();
  c.globalCompositeOperation = 'multiply';
  for (const r of runs) {
    if (r.x1 < v.x - 40 || r.x0 > v.x + v.w + 40 || r.y < v.y - 40 || r.y > v.y + v.h + 10) continue;
    const len = r.soft ? 3 + r.height * 1.5 : Math.min(40, 8 + r.height * 5) * reach;
    const skew = sun.dx * len * 1.1;
    const a = (r.soft ? 0.24 : 0.38) * sun.alpha;
    // Two stacked flat fills fake a gradient far more cheaply than a gradient per run per frame.
    for (const [k, f] of [[1, 0.45], [0.5, 0.55]] as const) {
      c.fillStyle = `rgba(40,30,70,${a * f})`;
      c.beginPath();
      c.moveTo(r.x0, r.y);
      c.lineTo(r.x1, r.y);
      c.lineTo(r.x1 + skew * k, r.y + len * k);
      c.lineTo(r.x0 + skew * k, r.y + len * k);
      c.closePath();
      c.fill();
    }
    // A crisp contact line right under the wall.
    c.fillStyle = `rgba(30,22,50,${a * 0.8})`;
    c.fillRect(r.x0, r.y, r.x1 - r.x0, 1.5);
  }
  c.restore();
}

// --- Birds crossing the sky ------------------------------------------------------------

interface Flock {
  x: number;
  y: number;
  vx: number;
  n: number;
  phase: number;
  spread: number;
}

/** Occasional flocks that cross the view high above the roofs. World coordinates. */
export class Flocks {
  private flocks: Flock[] = [];
  private next = 6 + Math.random() * 10;

  update(dt: number, v: View, biome: Biome, night: number): void {
    this.next -= dt;
    if (biome !== 'none' && night < 0.5 && this.next <= 0 && this.flocks.length < 2) {
      this.next = 18 + Math.random() * 30;
      const dir = Math.random() < 0.5 ? 1 : -1;
      this.flocks.push({
        x: dir > 0 ? v.x - 60 : v.x + v.w + 60,
        y: v.y + 30 + Math.random() * v.h * 0.4,
        vx: dir * (28 + Math.random() * 16),
        n: 3 + Math.floor(Math.random() * 6),
        phase: Math.random() * 10,
        spread: 0.8 + Math.random() * 0.6,
      });
    }
    for (let i = this.flocks.length - 1; i >= 0; i--) {
      const f = this.flocks[i];
      f.x += f.vx * dt;
      f.y -= 2 * dt;
      f.phase += dt;
      if (f.x < v.x - 160 || f.x > v.x + v.w + 160) this.flocks.splice(i, 1);
    }
  }

  draw(c: Ctx, t: number): void {
    for (const f of this.flocks) {
      const dir = Math.sign(f.vx);
      for (let i = 0; i < f.n; i++) {
        const row = Math.ceil(i / 2), side = i % 2 ? 1 : -1;
        const bx = f.x - dir * row * 9 * f.spread, by = f.y + (i ? side * row * 4 * f.spread : 0) + Math.sin(t * 2 + i) * 0.6;
        const flap = Math.sin(t * 9 + f.phase + i * 0.7);
        c.strokeStyle = 'rgba(30,25,45,0.85)';
        c.lineWidth = 0.9;
        c.beginPath();
        c.moveTo(bx - 3, by - flap * 1.6);
        c.lineTo(bx, by + 0.6);
        c.lineTo(bx + 3, by - flap * 1.6);
        c.stroke();
      }
      // Faint shadow of the flock racing over the ground.
      c.fillStyle = 'rgba(0,0,0,0.05)';
      c.fillRect(f.x - 14, f.y + 140, 28, 6);
    }
  }
}

// --- Colour grade -------------------------------------------------------------------

let grain: HTMLCanvasElement | null = null;
function grainPattern(c: Ctx): CanvasPattern | null {
  if (!grain) {
    grain = document.createElement('canvas');
    grain.width = grain.height = 96;
    const g = grain.getContext('2d')!;
    const img = g.createImageData(96, 96);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = 110 + Math.random() * 70;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
    g.putImageData(img, 0, 0);
  }
  return c.createPattern(grain, 'repeat');
}

/**
 * Painterly grade over the finished frame: cool sky light from above, warm bounce
 * from below, a golden-hour bloom from the sun's side, and a whisper of grain.
 */
export function drawGrade(c: Ctx, v: View, minutes: number, indoor: boolean, gloom: number): void {
  const night = nightOf(minutes);
  c.save();
  if (!indoor) {
    // Vertical light: the top of the view is lit by the sky, the bottom bounces warm.
    // Plain alpha blending: full-screen 'overlay' passes are too slow on phones.
    const g = c.createLinearGradient(0, v.y, 0, v.y + v.h);
    // At night the grade deepens the blues instead of lightening them: a navy wash that keeps lamps warm by contrast.
    const cool = night > 0.5 ? `rgba(12,16,60,${0.16 + night * 0.14})` : `rgba(150,190,255,${0.07 * (1 - gloom * 0.5)})`;
    const warm = night > 0.5 ? `rgba(8,6,30,${0.12 + night * 0.1})` : `rgba(255,190,120,${0.05 * (1 - gloom)})`;
    g.addColorStop(0, cool);
    g.addColorStop(1, warm);
    c.fillStyle = g;
    c.fillRect(v.x, v.y, v.w, v.h);
    // Golden hour: a warm bloom from the sun's side of the sky.
    const golden = Math.max(smoothstep(16 * 60, 17 * 60 + 40, minutes) * (1 - smoothstep(18 * 60 + 10, 19 * 60, minutes)), smoothstep(5 * 60 + 40, 6 * 60 + 40, minutes) * (1 - smoothstep(7 * 60, 8 * 60, minutes)));
    if (golden > 0.01 && gloom < 0.8) {
      c.globalCompositeOperation = 'screen';
      const sx = sun.dx < 0 ? v.x + v.w * 1.1 : v.x - v.w * 0.1;
      const r = c.createRadialGradient(sx, v.y - v.h * 0.2, 10, sx, v.y - v.h * 0.2, v.w * 1.3);
      r.addColorStop(0, `rgba(255,160,70,${0.28 * golden * (1 - gloom)})`);
      r.addColorStop(1, 'rgba(255,120,60,0)');
      c.fillStyle = r;
      c.fillRect(v.x, v.y, v.w, v.h);
    }
  }
  // Grain: a little texture so flat colours never look like plastic (cheap alpha blend).
  const p = grainPattern(c);
  if (p) {
    c.globalAlpha = indoor ? 0.035 : 0.05;
    c.fillStyle = p;
    c.translate(v.x, v.y);
    c.fillRect(0, 0, v.w, v.h);
  }
  c.restore();
}
